import React, { useCallback, useMemo } from 'react';
import { useEmployeeEngagementDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { formatDashboardNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/formatDashboardNumber';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

const COLUMNS: DataTableColumn[] = [
  { key: 'teamMemberName', label: 'Team Member Name' },
  { key: 'cancelledCounts', label: 'Post-Accept Cancel Shifts', align: 'right', noWrap: true },
  { key: 'acceptCounts', label: 'Accepted Shifts', align: 'right', noWrap: true },
  { key: 'cancellationRate', label: 'Post-Accept Cancellation Rate', align: 'right', noWrap: true },
];

export const TopIndividualCancellationTable: React.FC = () => {
  const data = useEmployeeEngagementDashboardStore((s) => s.topCancellations);
  const currentPage = useEmployeeEngagementDashboardStore((s) => s.topCancellationsPage);
  const totalPages = useEmployeeEngagementDashboardStore((s) => s.topCancellationsTotalPages);
  const isLoading = useEmployeeEngagementDashboardStore((s) => s.topCancellationsLoading);
  const hasError = useEmployeeEngagementDashboardStore((s) => s.topCancellationsError);
  const fetchTopCancellations = useEmployeeEngagementDashboardStore((s) => s.fetchTopCancellations);

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchTopCancellations(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchTopCancellations, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        teamMemberName: d.teamMemberName,
        cancelledCounts: d.cancelledCounts,
        acceptCounts: d.acceptCounts,
        cancellationRate:
          d.cancellationRate == null ? '—' : `${formatDashboardNumber(d.cancellationRate)}%`,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return (
      <EmptyStateContainer>Failed to load top individual cancellation data</EmptyStateContainer>
    );
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Top individual cancellation table"
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
